import React from "react";
import ReactMapGL, {
  FullscreenControl,
  Popup,
  FlyToInterpolator,
} from "react-map-gl";
import MutationMapBoxStyle from "./mutationMapBox.module.css";

import { easeCubic } from "d3-ease";
import { extent } from "d3-array";

import { useInterval } from "../../util/useInterval";
import { getMutationMap } from "../../service/mapData";
import { getLatestStrain } from "../../service/latestStrainData";
import { removeMissingFields } from "../../util/parseTableData";
import Markers from "./markers";
import MarkersCountry from "./markersCountry";
const MutationMapBox = ({ variant, title, subtitle = "" }) => {
  // setup map
  const defaultViewport = React.useRef({
    latitude: 5,
    longitude: 6,
    width: "100%",
    height: "300px",
    zoom: 0.5,
  });
  const [viewport, setViewport] = React.useState(defaultViewport.current);

  // Tooltip controls
  const [showPopup, setShowPopup] = React.useState(false);
  const [latFocus, setLatFocus] = React.useState(0);
  const [lngFocus, setLngFocus] = React.useState(180);
  const [offsetFocus, setOffsetFocus] = React.useState(0);
  const [tooltip, setTooltip] = React.useState("");
  const [showMarkers, setShowMarkers] = React.useState(false);

  // Fullscreen controls
  const [fullScreen, setFullScreen] = React.useState(false);
  React.useEffect(() => {
    if (!fullScreen) {
      setViewport(defaultViewport.current);
    }
  }, [fullScreen]);

  const mapRef = React.useRef();

  // data processing
  const [minDomain, setMinDomain] = React.useState(-1);
  const [maxDomain, setMaxDomain] = React.useState(-1);
  const [minDomainCountry, setMinDomainCountry] = React.useState(-1);
  const [maxDomainCountry, setMaxDomainCountry] = React.useState(-1);
  const [data, setData] = React.useState([]);
  const [dataCountry, setDataCountry] = React.useState([]);
  const [recentSubmissionData, setRecentSubmissionData] = React.useState([]);
  const [recentSubmissionDataCountry, setRecentSubmissionDataCountry] = React.useState([]);

  function parseData(result) {
    let countryArray = [];
    if (result !== null && result.length > 0) {
      for (let i = 0; i < result.length; i++) {
        let itemi = result[i];
        let found = false;
        for (let j = 0; j < countryArray.length; j++) {
          let itemj = countryArray[j];
          if (itemi.country === itemj.country) {
            itemj.count += itemi.count;
            found = true;
          }
        }
        if (!found) {
          let copy = Object.assign({}, itemi);
          delete copy.city;
          countryArray.push(copy);
        }
      }
    }
    return countryArray;
  }

  React.useEffect(() => {
    getMutationMap(variant).then((result) => {
      setData(result);
      setDataCountry(parseData(result));
    });
    getLatestStrain(variant).then((result) => {
      setRecentSubmissionData(
        removeMissingFields(result).map((value) => value.city)
      );
      setRecentSubmissionDataCountry(result.map((value) => value.country));
    }
    );
  }, [variant]);

  React.useEffect(() => {
    const [lowerBound, upperBound] = extent(data.map((val) => val.count));
    setMinDomain(lowerBound);
    setMaxDomain(upperBound);
    const [lowerBoundCty, upperBoundCty] = extent(dataCountry.map((val) => val.count));
    setMinDomainCountry(lowerBoundCty);
    setMaxDomainCountry(upperBoundCty);
    // console.debug(lowerBoundCty,upperBoundCty);
    // const initFocus = data.find((value) => value.count === upperBound);
    const newViewport = {
      ...defaultViewport.current,
      latitude: parseFloat(
        defaultViewport.current.latitude
      ),
      longitude: parseFloat(
        defaultViewport.current.longitude
      ),
      zoom:defaultViewport.current.zoom,
      transitionDuration: 1000,
      transitionInterpolator: new FlyToInterpolator(),
      transitionEasing: easeCubic,
    };
    setViewport(newViewport);
    setShowMarkers(false);
  }, [data,dataCountry]);

  const fiveMinutes = 1000 * 60 * 5; // in milliseconds
  useInterval(() => {
    getMutationMap(variant).then((result) => {
      setData(result);
      setDataCountry(parseData(result));
    });
    getLatestStrain(variant).then((result) => {
      setRecentSubmissionData(result.slice(0, 5).map((value) => value.city));
      setRecentSubmissionDataCountry(result.map((value) => value.country));
    }
    );
  }, fiveMinutes);

  return (
    <div>
      <h2 className={MutationMapBoxStyle.title}>{title}</h2>
      <h3
        className={`${MutationMapBoxStyle.title} ${MutationMapBoxStyle.subtitle}`}
      >
        {subtitle}
      </h3>
      <ReactMapGL
        {...viewport}
        maxZoom={20}
        mapboxApiAccessToken={
          process.env.NODE_ENV === "production"
            ? process.env.REACT_APP_MAPBOX_TOKEN
            : process.env.REACT_APP_MAPBOX_TEST
        }
        mapStyle="mapbox://styles/mapbox/light-v10"
        onViewportChange={(newViewport) => {
          setViewport({ ...newViewport, width: "100%", height: "300px" });
          // console.debug(newViewport.zoom);
          if (newViewport.zoom > 0.5) {
            setShowMarkers(true);
          } else {
            setShowMarkers(false);
          }
        }}
        ref={mapRef}
      >
        <div
          style={{ position: "absolute", right: 0 }}
          onClick={() => setFullScreen(!fullScreen)}
        >
          <FullscreenControl container={document.querySelector("root")} />
        </div>
        {showMarkers ? (<Markers
          data={data}
          mapRef={mapRef}
          viewport={viewport}
          setViewport={setViewport}
          pulseCountries={recentSubmissionData}
          minCount={minDomain}
          maxCount={maxDomain}
          setShowPopup={setShowPopup}
          setLatFocus={setLatFocus}
          setLngFocus={setLngFocus}
          setOffsetFocus={setOffsetFocus}
          setTooltip={setTooltip}
        />) : (<MarkersCountry
          data={dataCountry}
          mapRef={mapRef}
          viewport={viewport}
          setViewport={setViewport}
          pulseCountries={recentSubmissionDataCountry}
          minCount={minDomainCountry}
          maxCount={maxDomainCountry}
          setShowPopup={setShowPopup}
          setLatFocus={setLatFocus}
          setLngFocus={setLngFocus}
          setOffsetFocus={setOffsetFocus}
          setTooltip={setTooltip}
        />)}
        {showPopup ? (
          <Popup
            latitude={latFocus}
            longitude={lngFocus}
            offsetTop={offsetFocus}
            closeButton={false}
            anchor="bottom"
          >
            <div>{tooltip}</div>
          </Popup>
        ) : null}
      </ReactMapGL>
    </div>
  );
};

export default MutationMapBox;
